import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Heart, MapPin } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Podcast } from '@/types/Podcast';

interface LocationSuggestionsProps {
  podcasts: Podcast[];
  favorites: string[];
  onLocationClick: (query: string) => void;
  language: 'FR' | 'EN';
  t: (key: string) => string;
  selectedCategories?: string[];
  onCategoryToggle?: (category: string) => void;
}

interface CategoryCount {
  category: string;
  count: number;
}

const LocationSuggestions: React.FC<LocationSuggestionsProps> = ({
  podcasts,
  favorites,
  onLocationClick,
  language,
  t,
  selectedCategories = [],
  onCategoryToggle
}) => {
  const [popularCategories, setPopularCategories] = useState<CategoryCount[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPopularCategories = async () => {
      try {
        const { data, error } = await supabase
          .from('podcasts')
          .select('Type_Google')
          .not('Type_Google', 'is', null)
          .not('Type_Google', 'eq', '');

        if (error) throw error;

        // Unwanted categories to filter out
        const unwantedCategories = [
          'Tourist attraction',
          'Favorites', 
          '0ahUKEwiLiaPx99COAxXJJEQwaQDCA0oAA',
          'Want to go'
        ];

        // Count raw categories with grouping
        const categoryCount: { [key: string]: number } = {};
        
        // Define category groups
        const categoryGroups: { [key: string]: string[] } = {
          'Christian Landmarks': ['Catholic church', 'Church', 'Basilica', 'Cathedral', 'Catholic cathedral'],
          'Museum': ['Museum', 'Art museum']
        };
        
        data.forEach(item => {
          if (item.Type_Google) {
            // Split compound types
            const types = item.Type_Google.split(',').map(type => type.trim());
            
            types.forEach(type => {
              // Skip unwanted categories
              if (unwantedCategories.includes(type)) return;
              // Skip empty or very short categories
              if (type.length < 3) return;
              
              // Check if this type belongs to a group
              let groupName = type;
              for (const [group, members] of Object.entries(categoryGroups)) {
                if (members.includes(type)) {
                  groupName = group;
                  break;
                }
              }
              
              categoryCount[groupName] = (categoryCount[groupName] || 0) + 1;
            });
          }
        });

        // Convert to array and sort by count
        const sortedCategories = Object.entries(categoryCount)
          .map(([category, count]) => ({ category, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 12); // Take top 12 categories

        // Add Favorites as first category if it has items
        const finalCategories = favorites.length > 0 
          ? [{ category: 'Favorites', count: favorites.length }, ...sortedCategories]
          : sortedCategories;

        setPopularCategories(finalCategories);
      } catch (error) {
        console.error('Error fetching popular categories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPopularCategories();
  }, []);

  const handleFavoritesClick = () => {
    if (favorites.length === 0) {
      return; // Do nothing if no favorites
    }
    // Use special identifier for favorites filtering  
    onLocationClick('__favorites__');
  };

  const translateCategory = (category: string): string => {
    const translations: { [key: string]: { FR: string; EN: string } } = {
      'Favorites': { FR: 'Favoris', EN: 'Favorites' },
      'Art museum': { FR: 'Musée d\'art', EN: 'Art museum' },
      'Museum': { FR: 'Musée', EN: 'Museum' },
      'Christian Landmarks': { FR: 'Monuments Religieux', EN: 'Religious Landmarks' },
      'Historical landmark': { FR: 'Monument historique', EN: 'Historical landmark' },
      'Cathedral': { FR: 'Cathédrale', EN: 'Cathedral' },
      'Church': { FR: 'Église', EN: 'Church' },
      'Cave': { FR: 'Grotte', EN: 'Cave' },
      'Historical place': { FR: 'Lieu historique', EN: 'Historical place' },
      'Mosque': { FR: 'Mosquée', EN: 'Mosque' },
      'Stadium': { FR: 'Stade', EN: 'Stadium' },
      'Cultural landmark': { FR: 'Monument culturel', EN: 'Cultural landmark' },
      'Basilica': { FR: 'Basilique', EN: 'Basilica' },
      'Buddhist temple': { FR: 'Temple bouddhiste', EN: 'Buddhist temple' },
      'Catholic cathedral': { FR: 'Cathédrale catholique', EN: 'Catholic cathedral' },
      'Catholic church': { FR: 'Église catholique', EN: 'Catholic church' }
    };

    return translations[category]?.[language] || category;
  };

  const handleCategoryClick = (category: string) => {
    if (category === 'Favorites') {
      handleFavoritesClick();
    } else if (onCategoryToggle) {
      onCategoryToggle(category);
    } else {
      onLocationClick(category);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-8 bg-muted animate-pulse rounded-full px-4 min-w-[80px]" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {/* Popular Categories */}
        {popularCategories.map((category) => {
          const isSelected = selectedCategories.includes(category.category) || (category.category === 'Favorites' && selectedCategories.includes('__favorites__'));
          const isFavorites = category.category === 'Favorites';
          return (
            <Button
              key={category.category}
              variant={isSelected ? "default" : "outline"}
              size="sm"
              onClick={() => handleCategoryClick(category.category)}
              className={`flex items-center gap-2 whitespace-nowrap shadow-sm ${
                isSelected 
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              {isFavorites ? <Heart className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
              {translateCategory(category.category)}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default LocationSuggestions;